import React, { useCallback, useEffect, useRef, useState } from 'react'
import { X } from '@phosphor-icons/react'
import { useLocale } from 'src/lib/hooks/useLocal'

const PILL_CLASS =
  'flex h-[26px] whitespace-nowrap items-center gap-1 px-1.5 rounded-md text-text-sm-medium text-zinc-600 transition-colors bg-zinc-950/5 hover:bg-zinc-950/8 shrink-0'

const PILL_SELECTED_CLASS =
  'flex h-[26px] whitespace-nowrap items-center gap-1 px-1.5 rounded-md text-text-sm-medium text-zinc-700 transition-colors bg-zinc-950/10 shrink-0'

export type TagInputVariant = 'number' | 'string' | 'both'

interface TagInputProps {
  value: string
  onChange: (value: string) => void
  /** number = digits only (pincode, phone); string = any text (name, user id); both = any input */
  variant?: TagInputVariant
  placeholder?: string
  error?: boolean
  className?: string
  /** Focus the input when the component mounts (e.g. when dropdown opens) */
  autoFocus?: boolean
  /** Single-row height instead of the 128px block (e.g. inline condition rows) */
  compact?: boolean
  /** Hide the built-in focus hint when the parent renders its own hints */
  hideHint?: boolean
  onFocus?: () => void
  onBlur?: () => void
}

const parseValue = (
  str: string,
  treatAllAsPills: boolean = false
): { pills: string[]; currentInput: string } => {
  if (!str?.trim()) return { pills: [], currentInput: '' }
  const parts = str.split(/[\n,]/).map(s => s.trim()).filter(Boolean)
  if (treatAllAsPills || parts.length === 0) {
    return { pills: parts, currentInput: '' }
  }
  const endsWithDelimiter = /[\n,]\s*$/.test(str)
  if (endsWithDelimiter) {
    return { pills: parts, currentInput: '' }
  }
  if (parts.length === 1) {
    return { pills: [], currentInput: parts[0] }
  }
  return {
    pills: parts.slice(0, -1),
    currentInput: parts[parts.length - 1] || ''
  }
}

const TagInput: React.FC<TagInputProps> = ({
  value,
  onChange,
  variant = 'both',
  placeholder,
  error,
  className = '',
  autoFocus = false,
  compact = false,
  hideHint = false,
  onFocus,
  onBlur
}) => {
  const { t } = useLocale()
  const editableRef = useRef<HTMLDivElement>(null)
  const isNumeric = variant === 'number'

  const getTextFromNode = useCallback(
    (el: HTMLElement): string => {
      const raw = (el.textContent || '').trim()
      return isNumeric ? raw.replace(/[^\d]/g, '') : raw
    },
    [isNumeric]
  )

  useEffect(() => {
    if (autoFocus) {
      const timer = requestAnimationFrame(() => editableRef.current?.focus())
      return () => cancelAnimationFrame(timer)
    }
  }, [autoFocus])

  const initial = parseValue(value, Boolean(value?.trim()))
  const [pills, setPills] = useState<string[]>(() => initial.pills)
  const [currentInput, setCurrentInput] = useState<string>(() => initial.currentInput)
  const [isFocused, setIsFocused] = useState(false)
  const [allSelected, setAllSelected] = useState(false)
  const historyRef = useRef<Array<{ pills: string[]; currentInput: string }>>([])

  const pushHistory = useCallback((p: string[], c: string) => {
    historyRef.current = [...historyRef.current, { pills: p, currentInput: c }].slice(-50)
  }, [])

  const syncToParent = useCallback(
    (newPills: string[], newInput: string) => {
      const combined = [...newPills, newInput].filter(Boolean)
      onChange(combined.join('\n'))
    },
    [onChange]
  )

  useEffect(() => {
    const el = editableRef.current
    if (!el) return
    if (!currentInput) {
      // Contenteditable inserts a <br> when cleared; :empty placeholder only shows on a truly empty node.
      if (el.innerHTML !== '') el.replaceChildren()
      return
    }
    const domText = getTextFromNode(el)
    if (domText !== currentInput) {
      el.textContent = currentInput
      const range = document.createRange()
      const sel = window.getSelection()
      range.selectNodeContents(el)
      range.collapse(false)
      sel?.removeAllRanges()
      sel?.addRange(range)
    }
  }, [currentInput, pills.length, getTextFromNode])

  useEffect(() => {
    const combined = [...pills, currentInput].filter(Boolean).join('\n')
    if (value !== combined) {
      const { pills: p, currentInput: c } = parseValue(value, true)
      setPills(p)
      setCurrentInput(c)
    }
  }, [value])

  const commitCurrentInput = (el: HTMLDivElement) => {
    const trimmed = getTextFromNode(el)
    if (trimmed) {
      pushHistory(pills, trimmed)
      const newPills = [...pills, trimmed]
      setPills(newPills)
      setCurrentInput('')
      el.textContent = ''
      syncToParent(newPills, '')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
      e.preventDefault()
      const history = historyRef.current
      if (history.length === 0) return
      const prev = history[history.length - 1]
      historyRef.current = history.slice(0, -1)
      setPills(prev.pills)
      setCurrentInput(prev.currentInput)
      syncToParent(prev.pills, prev.currentInput)
      setAllSelected(false)
      return
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
      if (pills.length > 0) {
        e.preventDefault()
        setAllSelected(true)
      }
      return
    }

    if (allSelected) {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault()
        pushHistory(pills, currentInput)
        setPills([])
        setCurrentInput('')
        e.currentTarget.textContent = ''
        syncToParent([], '')
        setAllSelected(false)
        return
      }
      setAllSelected(false)
    }

    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commitCurrentInput(e.currentTarget)
      return
    }
    if (e.key === 'Backspace') {
      const text = getTextFromNode(e.currentTarget)
      if (!text && pills.length > 0) {
        e.preventDefault()
        pushHistory(pills, '')
        const newPills = pills.slice(0, -1)
        setPills(newPills)
        syncToParent(newPills, '')
      }
    }
  }

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const raw = el.textContent || ''
    const filtered = isNumeric ? raw.replace(/[^\d]/g, '') : raw
    if (raw !== filtered) {
      el.textContent = filtered
      const range = document.createRange()
      const sel = window.getSelection()
      range.selectNodeContents(el)
      range.collapse(false)
      sel?.removeAllRanges()
      sel?.addRange(range)
    }
    setCurrentInput(filtered)
    syncToParent(pills, filtered)
    if (!filtered) el.replaceChildren()
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const raw = e.clipboardData.getData('text')
    const parts = raw
      .split(/[\n\r,;\t•·–—\-\*\s]+/)
      .map(s => (isNumeric ? s.replace(/[^\d]/g, '').trim() : s.trim()))
      .filter(Boolean)

    if (parts.length > 1) {
      pushHistory(pills, currentInput)
      const newPills = [...pills, ...parts.slice(0, -1)]
      const last = parts[parts.length - 1]
      setPills(newPills)
      setCurrentInput(last)
      syncToParent(newPills, last)
    } else if (parts.length === 1) {
      const newInput = currentInput + parts[0]
      setCurrentInput(newInput)
      syncToParent(pills, newInput)
    }
  }

  const defaultPlaceholder =
    placeholder ??
    (isNumeric
      ? t('segment_creation.tag_input_placeholder_pincode')
      : t('segment_creation.tag_input_placeholder_string'))

  return (
    <>
      <div
        onClick={() => {
          setAllSelected(false)
          editableRef.current?.focus()
        }}
        data-error={Boolean(error)}
        className={`flex flex-wrap ${compact ? 'content-center' : 'content-start'} items-center gap-x-1 gap-y-1 w-full ${compact ? 'min-h-[36px] py-1 pl-1 pr-2 rounded-[8px]' : 'min-h-[128px] px-3 py-2.5 rounded-[10px]'} bg-white cursor-text transition-all outline-none input-outline [&:hover:not(:focus-within)]:input-outline-hover active:input-outline-active focus-within:input-outline-focus focus-within:!bg-white data-[error=true]:input-outline-error ${className}`}
      >
        {pills.map((pill, index) => (
          <span key={`${pill}-${index}`} className={allSelected ? PILL_SELECTED_CLASS : PILL_CLASS}>
            {pill}
            <X
              size={10}
              weight='bold'
              className='text-zinc-400 cursor-pointer hover:text-zinc-600 transition-colors shrink-0'
              onClick={e => {
                e.stopPropagation()
                pushHistory(pills, currentInput)
                const newPills = pills.filter((_, i) => i !== index)
                setPills(newPills)
                syncToParent(newPills, currentInput)
              }}
            />
          </span>
        ))}
        <div
          ref={editableRef}
          contentEditable
          suppressContentEditableWarning
          inputMode={isNumeric ? 'numeric' : 'text'}
          data-placeholder={pills.length === 0 ? defaultPlaceholder : ''}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onPaste={handlePaste}
          onFocus={() => {
            setIsFocused(true)
            onFocus?.()
          }}
          onBlur={() => {
            setIsFocused(false)
            setAllSelected(false)
            onBlur?.()
          }}
          className={`flex-1 min-w-[80px] min-h-[1.25rem] text-text-sm-regular text-coal-dark bg-transparent outline-none break-all whitespace-pre-wrap empty:before:content-[attr(data-placeholder)] empty:before:text-zinc-400 empty:before:pointer-events-none ${compact ? 'pl-1' : ''}`}
        />
      </div>
      {!hideHint && (
        <p
          className={`mt-1.5 flex text-text-xs-regular px-1 text-zinc-500 transition-all duration-200 ${
            isFocused ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1 pointer-events-none'
          }`}
        >
          {t('segment_creation.pill_input_hint')}
        </p>
      )}
    </>
  )
}

export default TagInput
